import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from 'ahooks';
import { TWO_HOURS_IN_MILLISECONDS, TWO_MINUTES_IN_MILLISECONDS } from 'mdc-constants';
import { useGraphQLClient } from 'providers';
import { useMemo } from 'react';
import {
  FileCard,
  FilesCardQuery,
  FilesCardQueryVariables,
  SortOrder,
  useAccessToken,
  useInfiniteFilesCardQuery,
} from 'services';

const defaultVariables: FilesCardQueryVariables = {
  page: 1,
  is_filepicker: false,
  orderBy: [{ column: 'created_at', order: SortOrder.Desc }],
};

export const useInfiniteFilesCard = (props: FilesCardQueryVariables) => {
  const { data: accessToken } = useAccessToken();
  const { graphQLClient } = useGraphQLClient(accessToken);

  const variables = useMemo(() => ({ ...defaultVariables, ...props }), [props]);
  const oldFolderId = useDebounce(variables.folder_id, { wait: 300 });

  const isFolderIdChanged = oldFolderId !== variables.folder_id;

  const result = useInfiniteFilesCardQuery(graphQLClient, variables, {
    gcTime: TWO_HOURS_IN_MILLISECONDS,
    staleTime: TWO_MINUTES_IN_MILLISECONDS,
    enabled: !!accessToken,
    placeholderData: (prev) => (!isFolderIdChanged ? keepPreviousData(prev) : undefined),
    initialPageParam: { ...variables, page: 1 },
    getNextPageParam: ({ files }) => {
      const { currentPage = 1, hasMorePages } = files?.paginatorInfo || {};
      if (hasMorePages) {
        return { ...variables, page: currentPage + 1 };
      }
    },
  });

  const files = useMemo<FileCard[]>(() => {
    return (
      result.data?.pages.reduce(
        (acc: FileCard[], paginatedResponse: FilesCardQuery) => [
          ...acc,
          ...(paginatedResponse.files?.data as FileCard[]),
        ],
        [],
      ) || []
    );
  }, [result]);

  return {
    ...result,
    files,
  };
};
